import { FilterValue, SorterResult } from 'antd/es/table/interface';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useItemListQuery } from 'api';
import { TablePaginationConfig, TableProps } from 'components';
import { dateFormatter } from 'constants/index';
import { ADMIN_ROUTES } from 'constants/routes';
import { OrderType, PaginationType, SortParamsType } from 'typings';
import { ItemDataType } from 'pages/admin/items/index';

export interface IFilterParams {
  name?: string;
  tags?: string[];
  countryId?: string;
  industryId?: string;
  createdDateFrom?: string;
  createdDateTo?: string;
  minPurchaseRequests?: number;
  maxPurchaseRequests?: number;
}

const initialPagination: PaginationType = { page: 1, size: 10 };
const initialSort: SortParamsType = {
  field: null,
  order: null,
};

export const useItems = () => {
  const [addModalState, setAddModalState] = useState<boolean>(false);
  const [drawerState, setDrawerState] = useState<boolean>(false);
  const [openFilters, setOpenFilters] = useState(false);
  const [suppliersData, setSuppliersData] = useState<Record<string, any>[] | undefined>();
  const [filters, setFilters] = useState<IFilterParams>({});
  const [pagination, setPagination] = useState<PaginationType>(initialPagination);
  const [sort, setSort] = useState<SortParamsType>(initialSort);
  const [search, setSearch] = useState<string | undefined>(undefined);

  const navigate = useNavigate();
  const { data: { content = [], totalElements = 0 } = {}, isLoading } = useItemListQuery({
    size: pagination.size,
    page: pagination.page,
    sortField: sort.field,
    sortOrder: sort.order,
    search,
    filters,
  });
  const onOpenFilters = useCallback(() => setOpenFilters(true), []);
  const onCloseFilters = useCallback(() => setOpenFilters(false), []);
  const onAddModalStateChange = useCallback((value: boolean) => setAddModalState(value), []);
  const routeChange = useCallback(
    (value: string) => {
      navigate(`${ADMIN_ROUTES.items.view}/${value}/suppliers`);
    },
    [navigate]
  );
  const onRedirectToBrand = useCallback(
    (value: string) => {
      navigate(`${ADMIN_ROUTES.brand.view}/${value}/management`);
    },
    [navigate]
  );
  const onRedirectToSupplier = useCallback(
    (id: string) => navigate(`${ADMIN_ROUTES.suppliers.view}/${id}/general`),
    [navigate]
  );

  const onExpand = useCallback((suppliers?: Record<string, any>[]) => {
    setSuppliersData(suppliers);
    setDrawerState((prevState) => !prevState);
  }, []);

  const handleTableChange: TableProps<ItemDataType>['onChange'] = useCallback(
    (
      pagination: TablePaginationConfig,
      _filters: Record<string, FilterValue | null>,
      sorter: SorterResult<ItemDataType> | SorterResult<ItemDataType>[]
    ) => {
      const sortField = Array.isArray(sorter) ? sorter[0].columnKey : sorter.columnKey;
      const sortOrder = Array.isArray(sorter) ? sorter[0].order : sorter.order;
      const order = sortOrder === 'ascend' ? 'asc' : sortOrder === 'descend' ? 'desc' : undefined;
      setPagination({
        page: pagination.current || 1,
        size: pagination.pageSize || 10,
      });
      setSort({
        field: sortField as string,
        order: order as OrderType,
      });
    },
    []
  );

  const onSearch = useCallback((value: string) => {
    setPagination((oState) => ({
      ...oState,
      page: 1,
    }));
    setSearch(value);
  }, []);

  const applyFilters = useCallback(
    (values: Record<string, any>) => {
      const _filters: any = {
        ...(values.brandId && { brandId: values.brandId }),
        ...(values.supplierId && { supplierId: values.supplierId }),
        ...(values.minAveragePrice && { minAveragePrice: values.minAveragePrice }),
        ...(values.maxAveragePrice && { maxAveragePrice: values.maxAveragePrice }),
        ...(values.createDate?.[0] && {
          createdDateFrom: dateFormatter(values.createDate[0], 'start'),
        }),
        ...(values.createDate?.[1] && {
          createdDateTo: dateFormatter(values.createDate[1], 'end'),
        }),
        ...(values.lastUpdatedDate?.[0] && {
          lastUpdatedDateFrom: dateFormatter(values.lastUpdatedDate[0], 'start'),
        }),
        ...(values.lastUpdatedDate?.[1] && {
          lastUpdatedDateTo: dateFormatter(values.lastUpdatedDate[1], 'end'),
        }),
      };
      setFilters(_filters);
      setPagination((oState) => ({
        ...oState,
        page: 1,
      }));
      onCloseFilters();
    },
    [onCloseFilters]
  );

  const resetFilters = useCallback(() => setFilters({}), []);

  return {
    onOpenFilters,
    onCloseFilters,
    onAddModalStateChange,
    addModalState,
    routeChange,
    data: content?.length ? content[0].items : [],
    total: totalElements ?? 0,
    handleTableChange,
    openFilters,
    applyFilters,
    resetFilters,
    pagination,
    isLoading,
    onSearch,
    onRedirectToSupplier,
    onRedirectToBrand,
    drawerState,
    onExpand,
    suppliersData,
  };
};
